import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Star, Quote, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ReviewForm - Dialog to submit a review
 */
export function ReviewForm({ trigger, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [jobTitle, setJobTitle] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error("Veuillez sélectionner une note");
      return;
    }
    
    if (comment.length < 10) {
      toast.error("Votre commentaire doit contenir au moins 10 caractères");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          rating,
          comment,
          company_name: companyName,
          job_title: jobTitle
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setOpen(false);
        setRating(0);
        setComment('');
        setCompanyName('');
        setJobTitle('');
        onSuccess?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la soumission");
      }
    } catch (error) {
      toast.error("Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" data-testid="leave-review-btn">
            <Star className="w-4 h-4 mr-2" />
            Laisser un avis
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Donnez votre avis sur Ema</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Star Rating */}
          <div className="text-center py-4">
            <div className="flex justify-center gap-1 mb-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="p-1 transition-transform hover:scale-110"
                  data-testid={`star-${star}`}
                >
                  <Star 
                    className={`w-8 h-8 ${
                      star <= (hoveredRating || rating)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-zinc-300'
                    }`}
                  />
                </button>
              ))}
            </div>
            <p className="text-sm text-zinc-500">
              {rating === 0 ? 'Cliquez pour noter' :
               rating === 1 ? 'Très insatisfait' :
               rating === 2 ? 'Insatisfait' :
               rating === 3 ? 'Satisfait' :
               rating === 4 ? 'Très satisfait' :
               'Excellent !'}
            </p>
          </div>

          {/* Comment */}
          <div className="space-y-2">
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Partagez votre expérience avec Ema..."
              className="min-h-24"
              required
              data-testid="review-comment"
            />
          </div>

          {/* Optional fields */}
          <div className="grid grid-cols-2 gap-3">
            <Input
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Entreprise (optionnel)"
              data-testid="review-company"
            />
            <Input
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="Poste (optionnel)"
              data-testid="review-job-title"
            />
          </div>

          <Button 
            type="submit" 
            className="w-full"
            disabled={loading || rating === 0}
            data-testid="submit-review-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Envoi...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Envoyer mon avis
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * ReviewCard - Display a single review
 */
export function ReviewCard({ review, className = '' }) {
  return (
    <Card className={`card-flat ${className}`}>
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-lime-100 flex items-center justify-center flex-shrink-0">
            <Quote className="w-5 h-5 text-lime-600" />
          </div>
          <div className="flex-1">
            {/* Stars */}
            <div className="flex gap-0.5 mb-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star 
                  key={star}
                  className={`w-4 h-4 ${
                    star <= review.rating
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-zinc-200'
                  }`}
                />
              ))}
            </div>
            
            {/* Comment */}
            <p className="text-ink mb-3 italic">"{review.comment}"</p>
            
            {/* Author */}
            <div className="flex items-center gap-2 text-sm">
              <span className="font-medium text-ink">{review.user_name}</span>
              {review.job_title && (
                <>
                  <span className="text-zinc-300">•</span>
                  <span className="text-ink-muted">{review.job_title}</span>
                </>
              )}
              {review.company_name && (
                <>
                  <span className="text-zinc-300">•</span>
                  <span className="text-ink-muted">{review.company_name}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * ReviewsSection - Display reviews with stats
 */
export function ReviewsSection({ limit = 6 }) {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReviews();
  }, [limit]);

  const fetchReviews = async () => {
    try {
      const [reviewsRes, statsRes] = await Promise.all([
        fetch(`${API}/api/reviews?limit=${limit}`),
        fetch(`${API}/api/reviews/stats`)
      ]);

      if (reviewsRes.ok) setReviews(await reviewsRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-zinc-200 rounded w-1/3 mx-auto" />
            <div className="grid md:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-40 bg-zinc-100 rounded-lg" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (reviews.length === 0) {
    return null;
  }

  return (
    <section className="py-16 bg-zinc-50">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header with stats */}
        <div className="text-center mb-12">
          <h2 className="font-display font-bold text-3xl text-ink mb-4 tracking-tight" style={{ wordSpacing: '0.12em' }}>
            {t('reviews.title')}
          </h2>
          
          {stats && stats.total_reviews > 0 && (
            <div className="flex items-center justify-center gap-4">
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star 
                    key={star}
                    className={`w-5 h-5 ${
                      star <= Math.round(stats.average_rating)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-zinc-200'
                    }`}
                  />
                ))}
              </div>
              <span className="text-lg font-semibold text-ink">
                {stats.average_rating}/5
              </span>
              <Badge variant="outline" className="text-zinc-500">
                {stats.total_reviews} {t('reviews.reviewCount')}
              </Badge>
            </div>
          )}
        </div>

        {/* Reviews grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((review) => (
            <ReviewCard key={review.review_id} review={review} />
          ))}
        </div>
      </div>
    </section>
  );
}
